import * as Sentry from '@sentry/react';
import log from 'loglevel';

// Global configuration storage for Sentry settings
const globalSentryConfig = {
  minLevel: 'error' as 'error' | 'warning' | 'info',
  enabled: false // Track if Sentry is actually enabled
};

// Environment-based configuration
const isDev = import.meta.env.MODE === 'development';

// Get log level from environment or use defaults
const getLogLevel = () => {
  const envLogLevel = import.meta.env.VITE_LOG_LEVEL?.toLowerCase();
  const enableConsoleLogs = import.meta.env.VITE_ENABLE_CONSOLE_LOGS !== 'false';
  const enableProductionDebug = import.meta.env.VITE_ENABLE_PRODUCTION_DEBUG === 'true';
  
  // If console logs are disabled, silence everything
  if (!enableConsoleLogs) return log.levels.SILENT;
  
  // If in production but debug is enabled via env var
  if (!isDev && enableProductionDebug) return log.levels.DEBUG;
  
  // Use env var if provided
  if (envLogLevel) {
    switch(envLogLevel) {
      case 'trace': return log.levels.TRACE;
      case 'debug': return log.levels.DEBUG;
      case 'info': return log.levels.INFO;
      case 'warn': return log.levels.WARN;
      case 'error': return log.levels.ERROR;
      case 'silent': return log.levels.SILENT;
    }
  }
  
  // Default based on environment
  return isDev ? log.levels.DEBUG : log.levels.ERROR;
};

// Set the log level based on configuration
log.setLevel(getLogLevel());

// Fields that should be masked in logs
const SENSITIVE_FIELDS = [
  // Auth related
  'password', 'token', 'jwt', 'secret', 'key', 'auth', 'access_token', 'refresh_token',
  // User data
  'email', 'user_id', 'userId', 'phone', 'session', 'user', 'identity',
  // Payment
  'card', 'payment', 'stripe', 'billing', 'subscription'
];

// Generic type for objects that can be logged
type LoggableData = Record<string, unknown> | unknown[] | string | number | boolean | null | undefined;

/**
 * Masks sensitive data in objects before logging
 */
const maskSensitiveData = <T extends LoggableData>(data: T): T => {
  // Check if masking is disabled via environment variable
  const enableMasking = import.meta.env.VITE_ENABLE_LOG_MASKING !== 'false';
  if (!enableMasking) return data;
  
  if (!data || typeof data !== 'object') return data;
  
  const sanitized = JSON.parse(JSON.stringify(data));
  
  // Special check for JWT tokens - they start with ey and contain periods
  const isJwtToken = (value: unknown): boolean => {
    return typeof value === 'string' && 
           value.length > 20 && 
           value.startsWith('ey') && 
           value.split('.').length >= 2;
  };
  
  const redact = (obj: Record<string, unknown>) => {
    if (!obj || typeof obj !== 'object') return;
    
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      // Check if key contains sensitive fields
      if (SENSITIVE_FIELDS.some(field => key.toLowerCase().includes(field))) {
        obj[key] = '[REDACTED]';
      } 
      // Special case for JWT tokens that might not be in sensitive keys
      else if (isJwtToken(value)) {
        obj[key] = '[REDACTED:JWT]';
      }
      // For objects that might contain nested sensitive data
      else if (value && typeof value === 'object') {
        redact(value as Record<string, unknown>);
      }
    });
  };
  
  redact(sanitized as Record<string, unknown>);
  return sanitized as T;
};

/**
 * Initialize Sentry with appropriate configuration
 * This should be called once at application startup
 */
export const initSentry = () => {
  // Use DSN from environment variables or fall back to the provided one
  // Get Sentry configuration from environment variables
  const dsn = import.meta.env.VITE_SENTRY_DSN || "https://6eb58d3fbb16b1b8e609aa5ea39d1ace@o4509106803048448.ingest.us.sentry.io/4509106810322944";
  const environment = import.meta.env.MODE;
  
  // Only enable Sentry if error reporting is enabled or in production
  const enableReporting = import.meta.env.VITE_ENABLE_ERROR_REPORTING === 'true' || environment === 'production';
  
  // If error reporting is disabled, don't initialize Sentry at all
  if (!enableReporting) {
    log.info('Sentry disabled - error reporting is turned off');
    globalSentryConfig.enabled = false;
    return;
  }
  
  // Get performance monitoring configuration
  const enablePerformance = import.meta.env.VITE_ENABLE_SENTRY_PERFORMANCE !== 'false';
  const enableReplays = import.meta.env.VITE_ENABLE_SENTRY_REPLAY === 'true';
  
  // Get sampling rates from environment or use defaults
  const tracesSampleRate = parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || (isDev ? '1.0' : '0.1'));
  
  // Get minimum error level to send to Sentry
  const sentryMinLevel = import.meta.env.VITE_SENTRY_MIN_LEVEL || 'error';
  
  // Store this globally so logger methods can access it
  globalSentryConfig.minLevel = sentryMinLevel as 'error' | 'warning' | 'info';
  globalSentryConfig.enabled = true;
  
  Sentry.init({
    dsn: dsn,
    integrations: [
      // Only include performance monitoring integrations if enabled
      ...(enablePerformance ? [Sentry.browserTracingIntegration()] : []),
      // Only include session replay if explicitly enabled (privacy concern)
      ...(enableReplays ? [Sentry.replayIntegration()] : []),
      // Only include profiling if performance monitoring is enabled
      ...(enablePerformance ? [Sentry.browserProfilingIntegration()] : [])
    ],
    // Performance monitoring sample rate from environment variable
    tracesSampleRate: enablePerformance ? tracesSampleRate : 0,
    // Session replay only if explicitly enabled
    replaysSessionSampleRate: enableReplays ? (isDev ? 1.0 : 0.1) : 0,
    replaysOnErrorSampleRate: enableReplays ? 1.0 : 0,
    // Browser profiling sample rate 
    // (relative to tracesSampleRate, so actual rate is tracesSampleRate * profilesSampleRate)
    profilesSampleRate: enablePerformance ? (isDev ? 1.0 : 0.5) : 0,
    // Environment tracking
    environment: environment,
    // Don't capture personally identifiable information
    beforeSend(event) {
      // Filter out any PII that might be included
      return event;
    },
    // Only enable in environments where error reporting is turned on
    enabled: true, // Always true here since we check enableReporting above
    // Quiet down Sentry in development
    debug: isDev,
    // Set propagation targets for the Strykr API and any other services
    tracePropagationTargets: [
      "localhost", 
      /^https:\/\/strykr-api-staging\.up\.railway\.app/,
      /^\/api\//
    ],
    // Don't send personally identifiable information
    sendDefaultPii: false
  });
  
  log.info('Sentry initialized with tracing and profiling');
};

/**
 * Check if Sentry is enabled and available
 */
const isSentryEnabled = (): boolean => {
  return globalSentryConfig.enabled;
};

/**
 * Check if a log level should be sent to Sentry based on configuration
 */
const shouldSendToSentry = (level: 'info' | 'warning' | 'error'): boolean => {
  const minLevel = globalSentryConfig.minLevel;
  
  if (minLevel === 'info') return true;
  if (minLevel === 'warning') return level === 'warning' || level === 'error';
  if (minLevel === 'error') return level === 'error';
  
  return false;
};

/**
 * Enhanced logger with Sentry integration
 */
export const logger = {
  debug: (message: string, extra?: Record<string, unknown>) => {
    log.debug(message, extra);
  },
  
  info: (message: string, extra?: Record<string, unknown>) => {
    log.info(message, extra);
  },
  
  warn: (message: string, extra?: Record<string, unknown>) => {
    log.warn(message, extra);
    
    // Only send to Sentry if enabled and meets minimum level
    if (isSentryEnabled() && shouldSendToSentry('warning')) {
      Sentry.addBreadcrumb({
        message,
        level: 'warning',
        data: extra
      });
    }
  },
  
  error: (message: string, error?: Error | unknown, extra?: Record<string, unknown>) => {
    log.error(message, error, extra);
    
    // Only send to Sentry if enabled and meets minimum level
    if (isSentryEnabled() && shouldSendToSentry('error')) {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: { source: 'logger' },
          extra: { message, ...extra }
        });
      } else {
        Sentry.captureMessage(message, 'error');
        if (extra) {
          Sentry.addBreadcrumb({
            message,
            level: 'error',
            data: extra
          });
        }
      }
    }
  },
  
  // Utility methods for Sentry
  setUser: (user: { id: string; email?: string; username?: string }) => {
    if (isSentryEnabled()) {
      Sentry.setUser(user);
    }
  },
  
  setTag: (key: string, value: string) => {
    if (isSentryEnabled()) {
      Sentry.setTag(key, value);
    }
  },
  
  setContext: (key: string, context: Record<string, unknown>) => {
    if (isSentryEnabled()) {
      Sentry.setContext(key, context);
    }
  },
  
  addBreadcrumb: (breadcrumb: { message: string; level?: 'info' | 'warning' | 'error'; data?: Record<string, unknown> }) => {
    if (isSentryEnabled()) {
      Sentry.addBreadcrumb(breadcrumb);
    }
  }
};

// Export Sentry directly for React error boundary
export { Sentry };
